/**
 * Admin prompts — typed api-client calls + TanStack Query hooks (E05-F02-T02).
 *
 * Feature-owned (NOT in the shared queryKeys.ts / queries.ts). The transport
 * uses the shared `apiClient` (auto-injects the session Bearer token, normalizes
 * the response, never throws) and `unwrap` (the single throw-at-the-Query-
 * boundary helper). Contracts come from `@eer/shared` so the SPA speaks the
 * exact same shapes as the Fastify API.
 *
 * Endpoints (1:1 with the legacy n8n manage_prompts get_prompts / update_prompt):
 *   GET   /admin/prompts            → usePrompts()
 *   PATCH /admin/prompts/:promptKey → useUpdatePrompt()
 */
import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryResult,
  type UseMutationResult,
} from '@tanstack/react-query';
import {
  adminPromptsListResponseSchema,
  adminPromptUpdateResponseSchema,
  type AdminPromptDTO,
  type AdminPromptUpdateBody,
  type AdminPromptUpdateResponse,
} from '@eer/shared';
import { apiClient, unwrap, type ApiResultError } from '../api/client.js';

/** Feature-local query key (kept out of the shared registry). */
export const PROMPTS_QUERY_KEY = ['admin', 'prompts'] as const;

/** Fetch all prompts (ordered stage ASC, prompt_key ASC by the server). */
async function fetchPrompts(): Promise<AdminPromptDTO[]> {
  const result = await apiClient.get<unknown>('/admin/prompts', { command: 'manage_prompts' });
  const body = adminPromptsListResponseSchema.parse(unwrap(result));
  return body.prompts;
}

/** PATCH one prompt by prompt_key. Only the provided fields are sent. */
async function patchPrompt(args: {
  promptKey: string;
  patch: AdminPromptUpdateBody;
}): Promise<AdminPromptUpdateResponse> {
  const result = await apiClient.patch<unknown>(
    `/admin/prompts/${encodeURIComponent(args.promptKey)}`,
    args.patch,
    { command: 'manage_prompts' },
  );
  return adminPromptUpdateResponseSchema.parse(unwrap(result));
}

/** List query — the prompt editor grid. */
export function usePrompts(): UseQueryResult<AdminPromptDTO[], ApiResultError> {
  return useQuery<AdminPromptDTO[], ApiResultError>({
    queryKey: PROMPTS_QUERY_KEY,
    queryFn: fetchPrompts,
  });
}

/** Update mutation — invalidates the list so the saved edit re-renders. */
export function useUpdatePrompt(): UseMutationResult<
  AdminPromptUpdateResponse,
  ApiResultError,
  { promptKey: string; patch: AdminPromptUpdateBody }
> {
  const queryClient = useQueryClient();
  return useMutation<
    AdminPromptUpdateResponse,
    ApiResultError,
    { promptKey: string; patch: AdminPromptUpdateBody }
  >({
    mutationFn: patchPrompt,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: PROMPTS_QUERY_KEY });
    },
  });
}
